import type { DefaultValues, SubmitHandler } from 'react-hook-form';
import { useEffect } from 'react';
import { Checkbox, Labeled } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContentBlockFormBodyProps } from '~/components/molecules/ContentBlock';
import type { IContainerProps } from '~/components/utils/Container';
import type { IFormMode } from '~/helpers/types';
import { FormField } from '~/components/atoms/FormField';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { usePersistentForm } from '~/components/utils/PersistentForm';
import { useFormAutoFocus } from '~/hooks/useFormAutoFocus';
import { IapPollFormMainSection } from './IapPollFormMainSection';

export type IIapPollFormValues = {
  title?: string;
  description?: string;
  options?: Array<{
    optionId?: string;
    priceId?: string;
    points?: number;
    maxVotesPerUserPerArticle?: number;
  }>;
  startDate?: string;
  endDate?: string;
  displayResults?: boolean;
  inactive?: boolean;
};

export type IIapPollFormProps = IContainerProps &
  Omit<
    IContentBlockFormBodyProps<IIapPollFormValues>,
    'form' | 'children' | 'onSubmit' | 'submitting'
  > & {
    mode: IFormMode;
    defaultValues?: DefaultValues<IIapPollFormValues>;
    onSubmit?: SubmitHandler<IIapPollFormValues>;
    onDisabledStatusChange?: (disabled: boolean) => void;
  };

export const IapPollForm: React.FC<IIapPollFormProps> = (props) => {
  const {
    sx,
    mode,
    defaultValues,
    onSubmit,
    onDisabledStatusChange,
    ...other
  } = props;
  const { t } = useTranslation();

  const form = usePersistentForm<IIapPollFormValues>({
    defaultValues,
  });
  useFormAutoFocus({ form, name: 'title' });

  const disabled = form.watch('inactive');
  useEffect(
    () => onDisabledStatusChange?.(!!disabled),
    [onDisabledStatusChange, disabled],
  );

  return (
    <ContentBlock.FormBody
      sx={sx}
      form={form}
      onSubmit={onSubmit}
      extraActions={
        <Labeled
          label={t('IapPollForm.fields.inactive.label')}
          labelPosition="right"
        >
          <FormField name="inactive" control={form.control} as={Checkbox} />
        </Labeled>
      }
      {...other}
    >
      <IapPollFormMainSection form={form} mode={mode} />
    </ContentBlock.FormBody>
  );
};
